import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import './MatchJoinHistory.css';

interface JoinHistoryItem {
  id: string;
  joinedAt: string;
  isSubstitute: boolean;
  joinMethod: string;
  playerName: string;
  user?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  player: {
    id: string;
    name: string;
    position: string;
    isSubstitute: boolean;
    teamId?: number;
  };
}

interface MatchJoinHistoryProps {
  matchId: string;
  matchName?: string;
  userIsCreator: boolean;
}

const MatchJoinHistory: React.FC<MatchJoinHistoryProps> = ({ matchId, matchName, userIsCreator }) => {
  const [joinHistory, setJoinHistory] = useState<JoinHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const fetchJoinHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/matches/${matchId}/join-history`);
      
      if (!response.ok) {
        throw new Error('Error al cargar el historial');
      }
      
      const data = await response.json();
      setJoinHistory(data.joinHistory);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    fetchJoinHistory();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const getJoinMethodLabel = (method: string) => {
    const methodLabels: { [key: string]: string } = {
      GOOGLE_OAUTH: 'Cuenta de Google',
      INVITE_LINK: 'Link de invitación',
      ADMIN_ADDED: 'Agregado por admin',
      WHATSAPP_BOT: 'Bot de WhatsApp',
      DIRECT_JOIN: 'Unión directa'
    };
    return methodLabels[method] || method;
  };

  // Si no es admin/creador, no mostrar nada
  if (!userIsCreator) {
    return null;
  }

  return (
    <>
      {/* Trigger — estilo MenuItem para no romper el layout del dropdown que lo
          contiene. El admin-only-button original (border 2px + gradiente + padding 12/24)
          se desbordaba del modal cuando se renderizaba adentro del menú. */}
      <button
        type="button"
        onClick={handleOpenModal}
        title="Solo visible para administradores del partido"
        style={{
          padding: '8px 12px',
          fontSize: '14px',
          color: '#f1f5f9',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          width: '100%',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span>📊</span>
        <span>Historial de uniones (admin)</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="join-history-modal-overlay" onClick={handleCloseModal} onKeyDown={(e) => { if (e.key === 'Escape') handleCloseModal(); }} role="presentation">
          <div className="join-history-modal" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            {/* Header del modal */}
            <div className="join-history-modal-header">
              <div className="modal-title-section">
                <h3>📊 Historial de Uniones</h3>
                {matchName && <p className="match-name">{matchName}</p>}
              </div>
              <button 
                onClick={handleCloseModal}
                className="modal-close-button"
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="join-history-modal-content">
              {loading && (
                <div className="modal-loading">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <p>Cargando historial...</p>
                </div>
              )}

              {error && (
                <div className="modal-error">
                  <p className="error-text">❌ {error}</p>
                  <button 
                    onClick={fetchJoinHistory}
                    className="btn btn-sm btn-outline-primary"
                  >
                    🔄 Reintentar
                  </button>
                </div>
              )}

              {!loading && !error && (
                <>
                  {/* Resumen de estadísticas */}
                  <div className="join-history-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total de uniones</span>
                      <span className="summary-value">{joinHistory.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Jugadores principales</span>
                      <span className="summary-value">
                        {joinHistory.filter(item => !item.isSubstitute).length}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Suplentes</span>
                      <span className="summary-value">
                        {joinHistory.filter(item => item.isSubstitute).length}
                      </span>
                    </div>
                  </div>

                  {/* Tabla de historial */}
                  {joinHistory.length === 0 ? (
                    <div className="no-history">
                      <div className="no-history-icon">📋</div>
                      <p>No hay uniones registradas para este partido.</p>
                      <small>Los jugadores aparecerán aquí cuando se unan al partido.</small>
                    </div>
                  ) : (
                    <div className="join-history-table-container">
                      <table className="join-history-table">
                        <thead>
                          <tr>
                            <th>Jugador</th>
                            <th>Fecha/Hora</th>
                          </tr>
                        </thead>
                        <tbody>
                          {joinHistory.map((item) => (
                            <tr key={item.id} className="join-history-row">
                              <td className="player-cell">
                                <div className="player-details-table">
                                  <span className="player-name-table">{item.playerName}</span>
                                  {item.user && (
                                    <span className="user-email-table">({item.user.email})</span>
                                  )}
                                </div>
                              </td>
                              <td className="time-cell">
                                <span className="join-time-table">
                                  {format(new Date(item.joinedAt), "dd/MM HH:mm", { locale: es })}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MatchJoinHistory;